import React from 'react';
import { motion, useScroll, useVelocity, useTransform, useSpring } from 'framer-motion';
import { Camera } from 'lucide-react';
import { usePhotos } from '../../lib/photos';
import Reveal from '../ui/Reveal';

// Photo ribbons that shear with scroll velocity — the page reacts to the reader.
function Ribbon({ items, reverse = false, skew }) {
  const row = [...items, ...items, ...items];
  return (
    <motion.div style={{ skewX: skew }} className="overflow-hidden">
      <div className={`${reverse ? 'marquee-reverse' : 'marquee'} flex w-max gap-5 py-3`}>
        {row.map((g, i) => (
          <div
            key={`${g.src}-${i}`}
            className={`relative shrink-0 overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-500 ${
              i % 3 === 0 ? 'rounded-[2.5rem]' : i % 3 === 1 ? 'rounded-[1.2rem]' : 'rounded-t-[3rem] rounded-b-[1.2rem]'
            } ${i % 2 ? 'rotate-[1.2deg]' : 'rotate-[-1.2deg]'}`}
          >
            <img
              src={g.src}
              alt={g.alt}
              loading="lazy"
              className="h-52 md:h-64 w-auto max-w-[34rem] object-contain hover:scale-[1.06] transition-transform duration-700"
            />
            <div aria-hidden className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-purple-500/15 via-transparent to-cyan-400/10 mix-blend-multiply" />
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export default function Gallery() {
  const { gallery } = usePhotos();
  const { scrollY } = useScroll();
  const velocity = useVelocity(scrollY);
  const skewRaw = useTransform(velocity, [-1400, 1400], [-3.5, 3.5]);
  const skew = useSpring(skewRaw, { stiffness: 220, damping: 28 });

  return (
    <section className="py-20 md:py-28 bg-white overflow-hidden">
      <Reveal className="max-w-7xl mx-auto px-4 text-center">
        <p className="text-xs md:text-sm font-extrabold uppercase tracking-[0.22em] text-pink-500 flex items-center justify-center gap-2">
          <Camera className="w-4 h-4" /> Fresh from the spa
        </p>
        <h2 className="mt-3 font-display text-4xl sm:text-5xl md:text-6xl font-black text-ink-900 leading-[1.02] tracking-tight">
          Happy clients,
          <span className="italic bg-gradient-to-r from-pink-500 to-cyan-500 bg-clip-text text-transparent"> fabulous fur.</span>
        </h2>
      </Reveal>

      <div className="mt-12 space-y-2 rotate-[-1deg] scale-[1.03]">
        <Ribbon items={gallery} skew={skew} />
        <Ribbon items={[...gallery].reverse()} reverse skew={skew} />
      </div>
    </section>
  );
}
